import { Capacitor } from '@capacitor/core';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { InlineStatus } from 'components/InlineStatus';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { StatItem, StatTable } from 'components/StatTable';
import { max, subHours } from 'date-fns';
import { useWeatherStationWithObservations } from 'features/weatherStations/hooks/useWeatherStationWithObservations';
import { useWeatherStationLatestObservation } from 'features/weatherStations/hooks/useWeatherStationLatestObservation';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getTimeAgo } from 'shared/dates';
import { formatNumber, getCurrentLocationAsShareUrl } from 'shared/utils';
import { useMemo } from 'react';
import { useTabs } from 'hooks/useTabs';
import { WeatherAlertList } from 'features/weatherAlerts/components/WeatherAlertList';
import { WeatherStationDataTable } from 'features/weatherStations/components/WeatherStationDataTable';
import { useWeatherStationPrecipitationEnabled } from 'features/weatherStations/hooks/useWeatherStationPrecipitationEnabled';
import { convertCelsiusToFahrenheit } from 'features/weatherStations/utils/convertCelsiusToFahrenheit';
import { parseNumericValue } from 'features/weatherStations/utils/parseNumericValue';
import { convertMetersPerSecToMph } from 'features/weatherStations/utils/convertMetersPerSecToMph';
import { useRequireMapLayerForRouterEntity } from 'hooks/useRequireMapLayerForRouterEntity';
import { MapLayers } from 'features/mapping/mapping.constants';
import { useWeatherStationState } from 'state/useWeatherStationState';
import {
  getHourAlignedDateRange,
  getIndicatorColorForSensorType,
  getObservationsHistoricalData,
} from './WeatherStationDetails.utils';
import { WindDirectionStat } from './WindDirectionStat';
import { WeatherStatValue } from './WeatherStatValue';
import {
  EMPTY_DATA_VALUE,
  WEATHER_OBSERVATIONS_POLL_INTERVAL,
  WEATHER_STATION_STALENESS_HOURS,
} from '../../constants';

const TAB_PANEL_ID_PREFIX = 'weather-station-tab-panel';

const CHART_HOURS = 24;

export const WeatherStationDetails = (): JSX.Element => {
  const { stationId } = useParams<{ stationId: string }>();
  const { t } = useTranslation();
  // Use hour-aligned boundaries for stable chart bucket alignment
  const { start: observationsHistoryStart, end: observationsHistoryEnd } =
    getHourAlignedDateRange(CHART_HOURS);
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const isPrecipitationEnabled = useWeatherStationPrecipitationEnabled();
  const { focusedSensorType } = useWeatherStationState();

  useRequireMapLayerForRouterEntity(MapLayers.WEATHER_STATIONS);

  const stationIdNum = parseInt(stationId, 10);

  const { weatherStation, observations, isLoading, error } =
    useWeatherStationWithObservations({
      id: stationIdNum,
      start: observationsHistoryStart,
      end: observationsHistoryEnd,
      pollInterval: WEATHER_OBSERVATIONS_POLL_INTERVAL,
      maxResults: CHART_HOURS,
    });

  const {
    latestObservation,
    isLoading: isLatestLoading,
    error: latestError,
  } = useWeatherStationLatestObservation({
    id: stationIdNum,
    pollInterval: WEATHER_OBSERVATIONS_POLL_INTERVAL,
  });

  const historicalData = useMemo(
    () =>
      // Uncomment to experiment with values and review the table UI behavior
      // getObservationsHistoricalData(
      //   (
      //     [
      //       { airTemp: 12 },
      //       { airTemp: 12 },
      //       { airTemp: 13 },
      //       { airTemp: 14 },
      //       { airTemp: 15 },
      //       { airTemp: 15 },
      //       { airTemp: 17 },
      //       { airTemp: 17 },
      //       { airTemp: 18 },
      //       { airTemp: 20 },
      //       { airTemp: undefined },
      //       { airTemp: 22 },
      //       { airTemp: 23 },
      //       { airTemp: 24 },
      //       { airTemp: 25 },
      //       { airTemp: 26 },
      //       { airTemp: 27 },
      //       { airTemp: 28 },
      //       { airTemp: 29 },
      //       { airTemp: 30 },
      //       { airTemp: 31 },
      //       { airTemp: 32 },
      //       { airTemp: 34 },
      //       { airTemp: 36 },
      //     ] as Partial<WeatherStationObservation>[]
      //   ).map((overrides, i) =>
      //     weatherStationObservationFactory.build({
      //       timestamp: subHours(new Date(), 23 - i),
      //       ...overrides,
      //     }),
      //   ),
      //   subHours(new Date(), 24),
      //   1,
      //   isPrecipitationEnabled,
      // ),
      getObservationsHistoricalData(
        observations,
        observationsHistoryStart,
        observationsHistoryEnd,
        1,
        focusedSensorType,
        isPrecipitationEnabled,
      ),
    [
      observations,
      observationsHistoryStart,
      observationsHistoryEnd,
      focusedSensorType,
      isPrecipitationEnabled,
    ],
  );

  const isOffline = Boolean(weatherStation?.isOffline);

  const elevationValue = parseFloat(weatherStation?.elevation ?? '');
  const elevationFeet = Number.isNaN(elevationValue)
    ? undefined
    : formatNumber(elevationValue, 0);

  const tabData = [
    {
      tabId: 'conditions',
      label: t('weatherStations.tabs.conditions'),
      enabled: true,
      content: (
        <Stack spacing={2}>
          {isOffline && (
            <Box sx={{ p: 2, pb: 0 }}>
              <Alert severity="error">
                <AlertTitle mb={0}>{t('weatherStations.offline')}</AlertTitle>
              </Alert>
            </Box>
          )}

          {historicalData ? (
            <Stack sx={{ pb: 0 }} spacing={1}>
              <WeatherStationDataTable data={historicalData} />
            </Stack>
          ) : null}
        </Stack>
      ),
    },
    {
      tabId: 'notifications',
      label: t('weatherStations.tabs.notifications'),
      enabled: Capacitor.isNativePlatform(),
      content: (
        <WeatherAlertList
          station={
            weatherStation
              ? { id: weatherStation.id, weatherStationType: 'atmospheric' }
              : undefined
          }
          fromRoute={`/weather_station/${weatherStation?.id}?tab=notifications`}
          showStationName={false}
        />
      ),
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('weatherStations.tabs.ariaLabel'),
    tabData,
  });

  const latestValues = latestObservation?.values;
  const latestTimestamps = latestObservation?.timestamps;

  // Filter out stale sensor values (older than staleness threshold)
  const freshValues = (() => {
    if (!latestValues || !latestTimestamps) return undefined;
    const threshold = subHours(new Date(), WEATHER_STATION_STALENESS_HOURS);

    return Object.fromEntries(
      Object.keys(latestValues).map((key) => {
        const ts = latestTimestamps[key as keyof typeof latestTimestamps];
        const isFresh = ts instanceof Date && ts > threshold;
        return [
          key,
          isFresh ? latestValues[key as keyof typeof latestValues] : undefined,
        ];
      }),
    ) as typeof latestValues;
  })();

  // Get the most recent timestamp from any sensor for "Updated" display
  const timestamps = Object.values(latestTimestamps ?? {}).filter(
    (ts): ts is Date => ts instanceof Date,
  );
  const mostRecentTimestamp =
    timestamps.length > 0 ? max(timestamps) : undefined;

  if (isLoading || isLatestLoading || error || latestError || !weatherStation) {
    return (
      <InlineStatus
        status={isLoading || isLatestLoading ? 'loading' : 'error'}
      />
    );
  }

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: t('home.weatherStationTitle', { name: weatherStation.name }),
      url: getCurrentLocationAsShareUrl(mostRecentTimestamp),
    });
  };

  const stats: StatItem[] = [
    {
      label: t('weatherStations.temp'),
      value: (
        <WeatherStatValue
          value={freshValues?.airTemp}
          i18nKey="weatherStations.tempValue"
          transValues={{
            temp: convertCelsiusToFahrenheit(freshValues?.airTemp ?? 0),
          }}
          valuePlaceholder={63}
          unitPlaceholder="˚F"
          indicatorColor={getIndicatorColorForSensorType(
            'airTemp',
            focusedSensorType,
            parseFloat(convertCelsiusToFahrenheit(freshValues?.airTemp ?? 0)),
          )}
        />
      ),
    },
    {
      label: t('weatherStations.dewPoint'),
      value: (
        <WeatherStatValue
          value={freshValues?.dewPointTemperature}
          i18nKey="weatherStations.tempValue"
          transValues={{
            temp: convertCelsiusToFahrenheit(
              freshValues?.dewPointTemperature ?? 0,
            ),
          }}
          valuePlaceholder={33}
          unitPlaceholder="˚F"
          indicatorColor={getIndicatorColorForSensorType(
            'dewPointTemperature',
            focusedSensorType,
            parseFloat(
              convertCelsiusToFahrenheit(freshValues?.dewPointTemperature ?? 0),
            ),
          )}
        />
      ),
    },
    {
      label: t('weatherStations.relHumidity'),
      value: (
        <WeatherStatValue
          value={freshValues?.relativeHumidity}
          i18nKey="weatherStations.percentageValue"
          transValues={{
            percent: parseNumericValue(freshValues?.relativeHumidity ?? 0),
          }}
          valuePlaceholder={50}
          unitPlaceholder="%"
          indicatorColor={getIndicatorColorForSensorType(
            'relativeHumidity',
            focusedSensorType,
            freshValues?.relativeHumidity ?? 0,
          )}
        />
      ),
    },
    {
      label: t('weatherStations.wind'),
      value: (
        <WeatherStatValue
          value={freshValues?.windSpeed}
          i18nKey="weatherStations.speedValue"
          transValues={{
            speed: convertMetersPerSecToMph(freshValues?.windSpeed ?? 0),
          }}
          valuePlaceholder={15}
          unitPlaceholder="mph"
          prefix={
            isPrecipitationEnabled &&
            typeof freshValues?.windDirectionToward === 'number' &&
            typeof freshValues?.windDirection === 'number' && (
              <Box sx={{ mr: 1 }}>
                <WindDirectionStat
                  windDirection={freshValues.windDirection}
                  windDirectionToward={freshValues.windDirectionToward}
                />
              </Box>
            )
          }
          indicatorColor={getIndicatorColorForSensorType(
            'windSpeed',
            focusedSensorType,
            freshValues?.windSpeed ?? 0,
          )}
        />
      ),
    },
    {
      label: t('weatherStations.gust'),
      value: (
        <WeatherStatValue
          value={freshValues?.windGust}
          i18nKey="weatherStations.speedValue"
          transValues={{
            speed: convertMetersPerSecToMph(freshValues?.windGust ?? 0),
          }}
          valuePlaceholder={25}
          unitPlaceholder="mph"
          indicatorColor={getIndicatorColorForSensorType(
            'windGust',
            focusedSensorType,
            freshValues?.windGust ?? 0,
          )}
        />
      ),
    },
    isPrecipitationEnabled
      ? {
          label: t('weatherStations.precipitation'),
          value: (
            <WeatherStatValue
              value={freshValues?.precipitation1Hr}
              i18nKey="weatherStations.precipitationValue"
              transValues={{
                value: parseNumericValue(freshValues?.precipitation1Hr ?? 0),
              }}
              valuePlaceholder={0.5}
              unitPlaceholder="in"
              indicatorColor={getIndicatorColorForSensorType(
                'precipitation1Hr',
                focusedSensorType,
                freshValues?.precipitation1Hr ?? 0,
              )}
            />
          ),
        }
      : {
          label: t('weatherStations.direction'),
          valueTypographyProps: { textTransform: 'capitalize' },
          value:
            typeof freshValues?.windDirectionToward === 'number' &&
            typeof freshValues?.windDirection === 'number' ? (
              <WindDirectionStat
                windDirection={freshValues.windDirection}
                windDirectionToward={freshValues.windDirectionToward}
              />
            ) : (
              EMPTY_DATA_VALUE
            ),
        },
  ];

  return (
    <>
      <Helmet>
        <title>
          {t('home.weatherStationTitle', { name: weatherStation.name })}
        </title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={weatherStation.name}
          subtitle={
            elevationFeet !== undefined
              ? t('weatherStations.elevation', { elevation: elevationFeet })
              : undefined
          }
        />

        <MapEntityDetailsLayout.Body>
          <StatTable stats={stats} />

          {mostRecentTimestamp && (
            <MapEntityDetailsLayout.Meta>
              <Typography variant="body2">
                {t('common.updated')}&nbsp;
                <b>{getTimeAgo(mostRecentTimestamp.toISOString())}</b>
              </Typography>
            </MapEntityDetailsLayout.Meta>
          )}

          {canShareOrCopy && (
            <MapEntityDetailsLayout.Actions>
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                onClick={handleShare}
                size="large"
              >
                {t('common.share')}
              </Button>
            </MapEntityDetailsLayout.Actions>
          )}
        </MapEntityDetailsLayout.Body>

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
